<template>
  <div class="novus-page">
    <header class="page-header">
      <div>
        <div class="eyebrow">
          NOVUS RESELLER
        </div>

        <h1>Customers</h1>

        <p>
          View customers and infrastructure provisioned
          through your reseller account.
        </p>
      </div>

      <button
        class="refresh-button"
        :disabled="loading"
        @click="fetchData">
        {{ loading ? 'Refreshing...' : 'Refresh' }}
      </button>
    </header>

    <div class="notice">
      <strong>Customer provisioning is managed by Novus Billing.</strong>
      Customer accounts and new virtual servers cannot be created
      from this panel.
    </div>

    <div v-if="error" class="message error">
      {{ error }}
    </div>

    <div class="summary-grid">
      <div class="summary-card">
        <span>Customers</span>
        <strong>{{ customers.length }}</strong>
        <small>Your customer accounts</small>
      </div>

      <div class="summary-card">
        <span>Virtual Servers</span>
        <strong>{{ totalServers }}</strong>
        <small>Across customers</small>
      </div>

      <div class="summary-card">
        <span>Running</span>
        <strong>{{ runningServers }}</strong>
        <small>Online now</small>
      </div>

      <div class="summary-card">
        <span>Allocated RAM</span>
        <strong>{{ formatRam(totalRam) }}</strong>
        <small>Across customers</small>
      </div>
    </div>

    <div class="toolbar">
      <div class="search-wrapper">
        <span class="search-icon">⌕</span>

        <input
          v-model.trim="search"
          type="text"
          placeholder="Search customers..."
          class="search-input">
      </div>

      <span class="result-count">
        {{ filteredCustomers.length }}
        {{ filteredCustomers.length === 1 ? 'customer' : 'customers' }}
      </span>
    </div>

    <div
      v-if="loading && customers.length === 0"
      class="message">
      Loading reseller customers...
    </div>

    <div
      v-else-if="!loading && filteredCustomers.length === 0"
      class="message">
      {{
        search
          ? 'No customers match your search.'
          : 'No customer accounts found.'
      }}
    </div>

    <div v-else class="customer-grid">
      <article
        v-for="customer in filteredCustomers"
        :key="customer.id"
        class="customer-card">

        <div class="customer-heading">
          <div class="avatar">
            {{ initials(customer.name) }}
          </div>

          <div class="identity">
            <h2>{{ customer.name }}</h2>

            <span>
              {{ customer.domain || 'Reseller Domain' }}
            </span>
          </div>

          <span
            class="account-state"
            :class="accountStateClass(customer.state)">
            {{ customer.state || 'Enabled' }}
          </span>
        </div>

        <div class="stats">
          <div>
            <span>Servers</span>
            <strong>
              {{ customerStats(customer).servers.length }}
            </strong>
          </div>

          <div>
            <span>Running</span>
            <strong>
              {{ customerStats(customer).running }}
            </strong>
          </div>

          <div>
            <span>vCPU</span>
            <strong>
              {{ customerStats(customer).cpu }}
            </strong>
          </div>

          <div>
            <span>Memory</span>
            <strong>
              {{ formatRam(customerStats(customer).ram) }}
            </strong>
          </div>
        </div>

        <div class="ip-section">
          <span class="section-label">
            Public IP Addresses
          </span>

          <div
            v-if="customerStats(customer).ips.length"
            class="ip-list">

            <code
              v-for="ip in customerStats(customer).ips"
              :key="ip">
              {{ ip }}
            </code>
          </div>

          <span v-else class="muted">
            No public IPs allocated
          </span>
        </div>

        <div class="actions">
          <button
            class="action primary"
            @click="openCustomerServers(customer)">
            View Virtual Servers
          </button>

          <button
            class="action"
            @click="toggleCustomer(customer)">
            {{
              expandedAccount === customer.id
                ? 'Hide Details'
                : 'Quick View'
            }}
          </button>
        </div>

        <transition name="details">
          <div
            v-if="expandedAccount === customer.id"
            class="customer-details">

            <div class="detail-row">
              <span>Account</span>
              <strong>{{ customer.name }}</strong>
            </div>

            <div class="detail-row">
              <span>Domain</span>
              <strong>{{ customer.domain || '—' }}</strong>
            </div>

            <div class="detail-row">
              <span>Account UUID</span>
              <code>{{ customer.id }}</code>
            </div>

            <div class="server-section">
              <div class="section-title">
                Virtual Servers
              </div>

              <div
                v-if="customerStats(customer).servers.length === 0"
                class="empty-servers">
                No virtual servers.
              </div>

              <div
                v-for="vm in customerStats(customer).servers"
                :key="vm.id"
                class="server-row">

                <div class="server-main">
                  <span
                    class="vm-dot"
                    :class="vm.state === 'Running'
                      ? 'running-dot'
                      : 'stopped-dot'">
                  </span>

                  <div>
                    <strong>
                      {{ vm.displayname || vm.name }}
                    </strong>

                    <code>
                      {{ primaryIp(vm) }}
                    </code>
                  </div>
                </div>

                <div class="server-meta">
                  <span
                    class="vm-state"
                    :class="vm.state === 'Running'
                      ? 'running-text'
                      : 'stopped-text'">
                    {{ vm.state }}
                  </span>

                  <span>
                    {{ vm.serviceofferingname || 'Virtual Server' }}
                  </span>

                  <span>
                    {{ vm.cpunumber || '—' }} vCPU
                  </span>

                  <span>
                    {{ formatRam(vm.memory) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </article>
    </div>
  </div>
</template>

<script>
import { listAll, resourceScope, isCustomerAccount, belongsToAccount, errorText } from '@/utils/novus'

export default {
  name: 'NovusCustomers',

  data () {
    return {
      loading: false,
      error: '',
      accounts: [],
      vms: [],
      search: '',
      expandedAccount: null
    }
  },

  computed: {
    userInfo () {
      return this.$store.getters.userInfo || {}
    },

    ownAccount () {
      return this.userInfo.account || ''
    },

    customers () {
      return this.accounts
        .filter(account => isCustomerAccount(account, this.userInfo))
        .sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
    },

    filteredCustomers () {
      if (!this.search) {
        return this.customers
      }

      const term = this.search.toLowerCase()

      return this.customers.filter(customer => {
        return [
          customer.name,
          customer.domain,
          customer.state
        ]
          .filter(Boolean)
          .some(value => {
            return String(value)
              .toLowerCase()
              .includes(term)
          })
      })
    },

    totalServers () {
      return this.vms.length
    },

    runningServers () {
      return this.vms.filter(vm => {
        return vm.state === 'Running'
      }).length
    },

    totalRam () {
      return this.vms.reduce((total, vm) => {
        return total + Number(vm.memory || 0)
      }, 0)
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    async fetchData () {
      this.loading = true
      this.error = ''

      try {
        if (this.userInfo.roletype !== 'DomainAdmin') {
          throw new Error('Customer management is available to reseller accounts.')
        }
        const scope = resourceScope(this.userInfo)
        const [accounts, vms] = await Promise.all([
          listAll('listAccounts', 'account', scope),
          listAll('listVirtualMachines', 'virtualmachine', { ...scope, details: 'nics,servoff' })
        ])
        this.accounts = accounts
        this.vms = vms.filter(vm => accounts.some(account =>
          isCustomerAccount(account, this.userInfo) && belongsToAccount(vm, account)))
      } catch (error) {
        this.error = this.errorText(
          error,
          'Unable to load reseller customers.'
        )
      } finally {
        this.loading = false
      }
    },

    customerVms (customer) {
      return this.vms.filter(vm => belongsToAccount(vm, customer))
    },

    customerStats (customer) {
      const servers = this.customerVms(customer)

      const running = servers.filter(vm => {
        return vm.state === 'Running'
      }).length

      const cpu = servers.reduce((total, vm) => {
        return total + Number(vm.cpunumber || 0)
      }, 0)

      const ram = servers.reduce((total, vm) => {
        return total + Number(vm.memory || 0)
      }, 0)

      const ips = []

      servers.forEach(vm => {
        const ip = this.primaryIp(vm)

        if (
          ip &&
          ip !== 'No public IP' &&
          !ips.includes(ip)
        ) {
          ips.push(ip)
        }
      })

      return {
        servers,
        running,
        cpu,
        ram,
        ips
      }
    },

    openCustomerServers (customer) {
      this.$router.push({
        path: '/novus-servers',
        query: {
          account: customer.name,
          domainid: customer.domainid
        }
      })
    },

    toggleCustomer (customer) {
      this.expandedAccount =
        this.expandedAccount === customer.id
          ? null
          : customer.id
    },

    primaryIp (vm) {
      if (vm.ipaddress) {
        return vm.ipaddress
      }

      if (vm.nic && vm.nic.length) {
        const primary =
          vm.nic.find(nic => nic.isdefault) ||
          vm.nic[0]

        return primary.ipaddress || 'No public IP'
      }

      return 'No public IP'
    },

    initials (name) {
      if (!name) {
        return '?'
      }

      const parts = name
        .split(/[\s_-]+/)
        .filter(Boolean)

      return parts
        .slice(0, 2)
        .map(part => {
          return part.charAt(0).toUpperCase()
        })
        .join('')
    },

    formatRam (memoryMb) {
      const value = Number(memoryMb || 0)

      if (!value) {
        return '—'
      }

      if (value >= 1024) {
        const gb = value / 1024

        return Number.isInteger(gb)
          ? `${gb} GB`
          : `${gb.toFixed(1)} GB`
      }

      return `${value} MB`
    },

    accountStateClass (state) {
      if (!state || state === 'enabled') {
        return 'enabled'
      }

      if (
        state === 'disabled' ||
        state === 'locked'
      ) {
        return 'disabled'
      }

      return 'other'
    },

    errorText
  }
}
</script>

<style scoped>
.novus-page {
  min-height: calc(100vh - 64px);
  padding: 30px;
  color: #dbe5f3;
  background: #080d16;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
  margin-bottom: 20px;
}

.eyebrow {
  color: #6fa2ff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .16em;
}

.page-header h1 {
  margin: 6px 0 4px;
  color: #fff;
  font-size: 29px;
}

.page-header p {
  margin: 0;
  color: #7789a2;
}

.refresh-button,
.action {
  cursor: pointer;
}

.refresh-button {
  padding: 9px 16px;
  color: #dce8ff;
  background: #163460;
  border: 1px solid #28548f;
  border-radius: 9px;
}

.refresh-button:disabled {
  cursor: not-allowed;
  opacity: .5;
}

.notice {
  padding: 14px 17px;
  margin-bottom: 20px;
  color: #8ba0bd;
  background: #0e1725;
  border: 1px solid #1e3049;
  border-radius: 11px;
}

.notice strong {
  color: #b8c9df;
}

.summary-grid {
  display: grid;
  grid-template-columns:
    repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 22px;
}

.summary-card {
  padding: 17px;
  background: #0e1725;
  border: 1px solid #1d2d44;
  border-radius: 12px;
}

.summary-card span {
  display: block;
  color: #7488a5;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.summary-card strong {
  display: block;
  margin: 5px 0 1px;
  color: #fff;
  font-size: 24px;
}

.summary-card small {
  color: #536780;
  font-size: 10px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 18px;
}

.search-wrapper {
  position: relative;
  width: min(420px, 100%);
}

.search-icon {
  position: absolute;
  top: 50%;
  left: 13px;
  color: #60758f;
  transform: translateY(-50%);
}

.search-input {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 12px 10px 36px;
  color: #d9e4f3;
  outline: none;
  background: #0d1724;
  border: 1px solid #20324a;
  border-radius: 9px;
}

.search-input:focus {
  border-color: #376496;
}

.search-input::placeholder {
  color: #51647e;
}

.result-count {
  color: #60758f;
  font-size: 11px;
}

.customer-grid {
  display: grid;
  grid-template-columns:
    repeat(auto-fill, minmax(360px, 1fr));
  gap: 17px;
}

.customer-card {
  padding: 20px;
  background:
    linear-gradient(
      155deg,
      #121e30,
      #0d1521
    );
  border: 1px solid #21324b;
  border-radius: 15px;
}

.customer-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  display: flex;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  color: #a9c9ff;
  background: #173258;
  border: 1px solid #284c78;
  border-radius: 11px;
  font-size: 13px;
  font-weight: 800;
}

.identity {
  min-width: 0;
  flex: 1;
}

.identity h2 {
  overflow: hidden;
  margin: 0;
  color: #fff;
  font-size: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.identity span {
  display: block;
  overflow: hidden;
  margin-top: 2px;
  color: #677c98;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-state {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
}

.account-state.enabled {
  color: #6ee19f;
  background: rgba(36, 173, 96, .13);
}

.account-state.disabled {
  color: #f39b9b;
  background: rgba(210, 63, 63, .13);
}

.account-state.other {
  color: #efcc7d;
  background: rgba(210, 155, 46, .13);
}

.stats {
  display: grid;
  grid-template-columns:
    repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding-top: 16px;
  margin-top: 17px;
  border-top: 1px solid #1d2b40;
}

.stats div {
  min-width: 0;
}

.stats span,
.section-label,
.detail-row span {
  display: block;
  color: #5d708b;
  font-size: 9px;
  letter-spacing: .05em;
  text-transform: uppercase;
}

.stats strong {
  display: block;
  overflow: hidden;
  margin-top: 3px;
  color: #d4dfed;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ip-section {
  padding-top: 15px;
  margin-top: 15px;
  border-top: 1px solid #1d2b40;
}

.ip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}

.ip-list code {
  padding: 4px 7px;
  color: #8db7ff;
  background: #101e31;
  border: 1px solid #213954;
  border-radius: 6px;
  font-size: 10px;
}

.muted {
  display: block;
  margin-top: 7px;
  color: #566a84;
  font-size: 10px;
}

.actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  margin-top: 17px;
  border-top: 1px solid #1d2b40;
}

.action {
  padding: 8px 11px;
  color: #bcd0ea;
  background: #152337;
  border: 1px solid #29415f;
  border-radius: 7px;
  font-size: 10px;
  font-weight: 700;
}

.action:hover {
  background: #1b2e48;
  border-color: #3b5e88;
}

.action.primary {
  color: #dbe9ff;
  background: #1e4b86;
  border-color: #346bb1;
}

.customer-details {
  padding-top: 16px;
  margin-top: 17px;
  border-top: 1px solid #22334b;
}

.detail-row {
  display: grid;
  grid-template-columns: 105px 1fr;
  gap: 12px;
  align-items: center;
  padding: 6px 0;
}

.detail-row strong,
.detail-row code {
  overflow: hidden;
  color: #cbd7e7;
  font-size: 10px;
  text-align: right;
  text-overflow: ellipsis;
}

.detail-row code {
  color: #8db7ff;
}

.server-section {
  margin-top: 15px;
}

.section-title {
  margin-bottom: 8px;
  color: #8195b0;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.server-row {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  padding: 11px;
  margin-top: 7px;
  background: #0a121e;
  border: 1px solid #18283b;
  border-radius: 9px;
}

.server-main {
  display: flex;
  min-width: 0;
  gap: 9px;
  align-items: center;
}

.server-main > div {
  min-width: 0;
}

.server-main strong {
  display: block;
  overflow: hidden;
  color: #d6e0ed;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.server-main code {
  display: block;
  margin-top: 2px;
  color: #739cd8;
  font-size: 9px;
}

.vm-dot {
  flex: 0 0 7px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.running-dot {
  background: #43c879;
}

.stopped-dot {
  background: #d96464;
}

.server-meta {
  display: grid;
  gap: 2px;
  color: #667b97;
  font-size: 9px;
  text-align: right;
}

.vm-state {
  font-weight: 800;
}

.running-text {
  color: #55ce86;
}

.stopped-text {
  color: #e07777;
}

.empty-servers {
  padding: 12px;
  color: #5e718b;
  background: #0a121e;
  border: 1px solid #18283b;
  border-radius: 8px;
  font-size: 10px;
}

.message {
  padding: 30px;
  color: #8191aa;
  text-align: center;
  background: #0e1623;
  border: 1px solid #1e2b40;
  border-radius: 13px;
}

.error {
  margin-bottom: 18px;
  color: #ffaaaa;
}

.details-enter-active,
.details-leave-active {
  transition: opacity .15s ease;
}

.details-enter,
.details-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 700px) {
  .novus-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }

  .customer-grid {
    grid-template-columns: 1fr;
  }

  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .stats {
    grid-template-columns: 1fr 1fr;
  }

  .server-row {
    flex-direction: column;
  }

  .server-meta {
    text-align: left;
  }

  .detail-row {
    grid-template-columns: 1fr;
  }

  .detail-row strong,
  .detail-row code {
    text-align: left;
  }
}
</style>
